import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { 
  Droplets,
  Ruler,
  Mountain,
  Timer,
  Building2,
  ChevronDown
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/retaining-wall-installation-nutley-nj.webp";
import morisTownshipImage from "@/assets/retaining-wall-morris-township-morris-county-nj.webp";
import wayneStepsImage from "@/assets/retaining-wall-steps-wayne-passaic-county-nj.webp";
import nutleyImage from "@/assets/retaining-wall-installation-nutley-nj-2.webp";
import franklinLakesImage from "@/assets/retaining-wall-installation-franklin-lakes-nj.webp";

const faqs = [
  {
    question: "Do I need a permit for a retaining wall in North Jersey?",
    answer: "Walls under four feet typically require a zoning permit only. Walls four feet or taller require construction permits and engineering approval in most New Jersey municipalities. Check with your local building department before starting.",
  },
  {
    question: "How deep should a retaining wall footing be in North Jersey?",
    answer: "Footings typically extend at least three feet below grade for stability. Frost line depth and flood hazard regulations in New Jersey affect the final requirement. We evaluate each property and follow state and local codes.",
  },
  {
    question: "What type of retaining wall lasts longest?",
    answer: "Concrete and natural stone walls last 50 to 100 years. Segmental block walls last 25 to 50 years or more. Timber walls have the shortest lifespan at 15 to 25 years.",
  },
  {
    question: "Can a retaining wall increase my home value?",
    answer: "Yes. A well-built retaining wall can add value by creating usable outdoor space and improving curb appeal. Buyers in North Jersey often view quality hardscaping as a sign that the property has been well maintained.",
  },
  {
    question: "What causes retaining walls to fail?",
    answer: "Poor drainage, inadequate base preparation, lack of reinforcement, and improper soil compaction are the primary causes. Most failures we see trace back to shortcuts during installation.",
  },
  {
    question: "What are alternatives to a retaining wall?",
    answer: "Options include terracing with multiple smaller walls, slope planting with deep-rooted vegetation, gabion baskets, geocell systems, and regrading the property. The right choice depends on the slope, soil, and how you intend to use the space.",
  },
];

const citations = [
  {
    id: 1,
    text: "FHWA (Federal Highway Administration), Design and Construction of Mechanically Stabilized Earth Walls and Reinforced Soil Slopes – Standards for reinforced retaining wall systems.",
    url: "https://www.fhwa.dot.gov/engineering/geotech/retaining/100317.cfm",
  },
  {
    id: 2,
    text: "NCMA (National Concrete Masonry Association), Segmental Retaining Wall Installation Guide and Best Practices – Base preparation and installation standards for segmental walls.",
    url: "https://ncma.org/",
  },
  {
    id: 3,
    text: "ICC (International Code Council), International Residential Code Section R404.4 – Retaining Walls – Building code requirements for residential retaining walls.",
    url: "https://codes.iccsafe.org/s/IRC2021P3/chapter-4-foundations/IRC2021P3-Pt03-Ch04-SecR404.4",
  },
  {
    id: 4,
    text: "Cornell Law Institute (LII), NJ Administrative Code § 7:13-12.13 – Requirements for Retaining Walls – New Jersey state regulations for retaining wall construction.",
    url: "https://www.law.cornell.edu/regulations/new-jersey/N-J-A-C-7-13-12-13",
  },
  {
    id: 5,
    text: "ASCE (American Society of Civil Engineers), Earth-Retaining Structures Design Standards (ASCE 7-16) – Engineering standards for earth-retaining structures.",
    url: "https://mylearning.asce.org/diweb/catalog/item?id=17045466",
  },
];

const youtubeVideoIds = ["cFHz4fwoL34", "uPmPXH1Vm4s", "RTdf5C02SHY"];

export default function RetainingWalls() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Retaining Wall Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Need a retaining wall on a slope? We handle drainage, base prep, and permits across North Jersey. Block, stone, or concrete. Free site visit included." />
        <meta property="og:description" content="Need a retaining wall on a slope? We handle drainage, base prep, and permits across North Jersey. Block, stone, or concrete. Free site visit included." />
        <meta property="og:title" content="Retaining Wall Installation Totowa, NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Retaining Wall Installation",
            description: "Professional retaining wall construction in North Jersey with proper drainage and base work.",
            url: "/masonry-services/retaining-walls",
            image: "https://masonry-pro.com/og/retaining-walls.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/retaining-walls.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/retaining-walls.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Retaining wall installation in Nutley, Essex County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Retaining Wall Installation in Totowa, NJ Serving North Jersey: Strong Walls That <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Protect Your Property</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Got a slope in your yard that's washing away? You're not alone. Sloped lots and hilly terrain are common across North Jersey. A retaining wall holds back the soil, stops erosion, and can turn a steep hillside into usable outdoor space. We handle the permits, design, and construction for properties throughout <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. Our walls meet NJ building codes and are built to stand for decades.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="/contact">Get a Free Site Evaluation</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="drainage-retaining-wall" packageName="Drainage & Retaining Wall Package" blurb={"Engineered wall, geogrid, drainage stone, and regrading bundled together so water and grade get solved at the same time, not patched twice."} />

          {/* Wall Height Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  Wall height is the number that changes everything on a retaining wall project. Once a wall goes over four feet, it usually has to be designed by an engineer with stamped plans, and it also calls for a lot more backfill, drainage stone, and geogrid running back into the slope. That is not an upsell, it is what keeps the wall standing. We build walls on both sides of that line across Totowa, Wayne, and Little Falls, and if someone is close to four feet we will tell them honestly whether dropping a few inches or stepping the wall makes more sense than paying for the engineering. As a <Link to="/" className="text-secondary hover:underline">masonry contractor in Totowa</Link>, we design every wall around the slope and drainage of your yard. See the walls in action in our <Link to="/videos/retaining-wall-wayne-nj" className="text-secondary hover:underline">Wayne retaining wall video</Link> and our <Link to="/videos/retaining-walls-paver-apron-kinnelon-nj" className="text-secondary hover:underline">Kinnelon driveway walls video</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Proper Drainage Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                      <Droplets className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      Proper Drainage Prevents <span className="text-gradient-gold">Retaining Wall Failure</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Water is the biggest threat to any retaining wall. When it builds up behind the structure, pressure starts to build. That's what causes walls to crack, bulge, or eventually give out altogether.
                    </p>
                    <p>
                      A lot of walls we get called to look at in Montclair, Wayne, and Morristown have the same problem. Either there's no drainage behind them, or it wasn't done right. The wall looked fine for a year or two, then started leaning. By that point, the damage is done. If your wall is already leaning or cracking, our <Link to="/masonry-services/retaining-wall-repair" className="text-secondary hover:underline">retaining wall repair</Link> work starts by finding out why it moved before anything is rebuilt.
                    </p>
                    <p>
                      We put gravel behind every wall we build so water has a path to escape. A pipe at the base directs it toward an outlet away from the structure. Fabric keeps soil from working its way into the gravel and clogging things up over time.
                    </p>
                    <p>
                      North Jersey gets over 45 inches of rain each year. Clay soils in <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link> and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties hold onto that moisture, which makes the pressure problem worse. Add in our freeze-thaw cycles, and a wall without proper drainage doesn't stand a chance.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={morisTownshipImage}
                    alt="Newly constructed modular retaining wall along a driveway in Morris Township, Morris County, NJ by Masonry Pro"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Engineering Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <Ruler className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Walls Over Four Feet <span className="text-gradient-gold">Require Engineering</span> in North Jersey
                  </h2>
                </div>
                
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Planning a wall that's four feet tall or higher? You'll need an engineer involved. New Jersey requires it. The state follows <a href="https://codes.iccsafe.org/s/IRC2021P3/chapter-4-foundations/IRC2021P3-Pt03-Ch04-SecR404.4" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">International Residential Code Section R404.4</a><sup>³</sup>, and walls at that height must have a licensed professional engineer sign off on the design.
                  </p>
                  <p>
                    This applies to a lot of projects in towns like Paramus, Ridgewood, and Hackensack. A homeowner wants to level out a slope for a patio or pool deck. From the yard it doesn't look like much, but the measurements put it over the threshold. You'll need permits from both the building and engineering departments before breaking ground.
                  </p>
                  <p>
                    A four-foot wall is holding back thousands of pounds of soil, and that load increases when it rains. The engineering determines how the wall handles that pressure without sliding forward or tipping over time. Footing depth, reinforcement, drainage. All of it gets calculated based on the conditions at your property.
                  </p>
                  <p>
                    Our team works with engineers regularly, and we can help coordinate that side of things. If you're unsure whether your project falls into that category, give us a call or text and we can take a look at the site.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Wall Types Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden order-last md:order-first">
                  <img
                    src={wayneStepsImage}
                    alt="Retaining wall with integrated steps in a backyard in Wayne, Passaic County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                      <Mountain className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      The Right Wall Type Depends on <span className="text-gradient-gold">Your Property's Slope</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Block, stone, poured concrete, and gabion are common methods for building retaining walls. Which one makes sense for your property? That depends on the slope and the soil.
                    </p>
                    <p>
                      We primarily build segmental block walls in Bergen and Essex Counties. They work well on residential lots with moderate grades and hold up through our winters without cracking.
                    </p>
                    <p>
                      Steeper terrain needs something stronger. Out in Ringwood and West Milford, we've had to anchor walls into the hillside with <a href="https://www.fhwa.dot.gov/engineering/geotech/retaining/100317.cfm" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">reinforcement</a><sup>¹</sup>. The grade out there doesn't forgive shortcuts.
                    </p>
                    <p>
                      Your yard might need something else entirely. We won't know until we see it. Give us a call or text, and we can walk the property with you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Lifespan Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <Timer className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Professional Installation Extends Wall Lifespan to <span className="text-gradient-gold">50+ Years</span>
                  </h2>
                </div>
                
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Concrete, stone, segmental block, and timber are common materials for retaining walls. How long each one lasts depends on the installation.
                  </p>
                  <p>
                    Concrete and stone walls typically last 50 to 100 years. Segmental block walls fall in the 25 to 50 year range. Timber walls have the shortest lifespan at 15 to 25 years.
                  </p>
                  <p>
                    We primarily build with segmental block and stone in <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> Counties. Both hold up well through our freeze-thaw cycles when installed with proper base work and drainage.
                  </p>
                  <p>
                    We've replaced walls that failed after just a few years. The materials weren't the problem. The base wasn't compacted. The drainage wasn't there. Those are the details that determine whether a wall lasts five years or fifty.
                  </p>
                  <p>
                      Your wall should outlast you. That's the standard we work toward. Give us a call or text, and we can talk through what a long-lasting wall looks like for your property.
                  </p>
                  <p>
                    A properly built retaining wall can be a meaningful investment, especially on larger or engineered projects. We offer financing through our partner Acorn Finance so you can spread the cost over time without paying everything up front. Checking your pre-qualified offers will not affect your credit score. <Link to="/financing" className="text-secondary hover:underline">Learn more about financing options</Link>.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Base and Footing Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      A Solid Base and Footing Keep <span className="text-gradient-gold">Your Wall Standing</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We primarily install walls in Hawthorne, Madison, and Short Hills where soil testing is often required before construction begins. The results indicate what the ground can handle and whether additional preparation is needed.
                    </p>
                    <p>
                      <a href="https://www.law.cornell.edu/regulations/new-jersey/N-J-A-C-7-13-12-13" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">New Jersey regulations</a> require footings to extend at least three feet below grade in flood hazard areas.<sup>⁴</sup> <a href="https://ncma.org/" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">NCMA guidelines</a> call for a minimum of six inches of compacted gravel base for most residential walls.<sup>²</sup> We follow both standards.
                    </p>
                    <p>
                      Walls that skip these steps tend to fail early. We've replaced retaining walls across North Jersey that were only a few years old. The blocks looked fine. The base underneath wasn't compacted, and there was no gravel to speak of.
                    </p>
                    <p>
                      Your wall sits on top of that base every single day. It carries the full load. Get it wrong and the problems show up fast. Get it right and the wall stays put for decades.
                    </p>
                    <p>
                      Give us a call, and we can walk through what proper base work looks like for your property.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={nutleyImage}
                    alt="New retaining wall installation with steps and railing in front of a light blue house in Nutley, Essex County, New Jersey"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>


          {/* Featured Snippet Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-card rounded-xl p-8 border border-border">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  Do I need drainage for a retaining wall in North Jersey?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Yes. Every retaining wall requires drainage to prevent water from accumulating behind it. Water trapped behind a wall exerts pressure, which over time causes cracking, bulging, and failure.
                </p>
                <p className="text-lg font-semibold mb-4">Proper drainage for a retaining wall includes:</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Gravel fill behind the wall to give water a path to escape</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>A perforated drain pipe at the base is angled toward a safe outlet</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Filter fabric over the gravel to prevent soil from clogging the system</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    <span>Surface grading that directs rainwater away from the wall</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-6">
                  Walls over four feet in North Jersey require engineered drainage plans. Clay soils common in Bergen and Passaic Counties hold water and need extra attention during installation.
                </p>
              </div>
            </div>
          </section>

          {/* Before & After Showcase */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Before &amp; After: <span className="text-gradient-gold">Franklin Lakes, Bergen County</span>
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                A complete retaining wall and staircase rebuild, from crumbling concrete to a clean, structurally sound installation.
              </p>
              <div className="max-w-2xl mx-auto rounded-xl overflow-hidden border border-border shadow-lg">
                <img
                  src={franklinLakesImage}
                  alt="Before and after of a concrete retaining wall and stairs in Franklin Lakes, Bergen County, NJ"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </section>
          <YouTubeShortsSection 
            title="Watch Our Retaining Wall Projects"
            videoIds={youtubeVideoIds}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" hubOverride={{ href: "/", label: "Masonry Contractor in North Jersey" }} />

          {/* CTA Section */}
          <CTASection 
            heading="Ready for a"
            highlightedHeading="Retaining Wall That Lasts?"
            serviceName="retaining wall"
            showFinancing
          />

          {/* Citations Section */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      <span className="flex items-center gap-2">
                        Citations & References
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                        {citations.map((citation) => (
                          <li key={citation.id}>
                            {citation.text}{" "}
                            <a 
                              href={citation.url} 
                              className="text-secondary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {citation.url}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
